<template>
  <MasterTable
    title="Spårantal"
    :rows="data"
    :columns="columns"
    :loading="loading"
    :add="add"
    :update="update"
    :remove="remove"
    :reset-form-model="resetFormModel"
    :form-model="model"
    :export-data="exportData"
    :force-show-add="true"
    :force-show-update="true"
    :force-show-delete="true"
    @update-form-model="(row) => (model = row)"
    remove-item-text-prop="spårantal"
    :copy="false"
  >
    <template #top-left>
      <q-btn
        class="q-ml-md"
        label="CSV Import"
        no-caps
        @click="() => (showCSVModal = true)"
      />

      <q-dialog v-model="showCSVModal" persistent>
        <q-card>
          <q-card-section class="text-body2 text-weight-bold">
            CSV Import spårantal
          </q-card-section>
          <q-card-section class="row items-center">
            <div class="row">
              <div class="col-12">Importera data CSV format:</div>
              <div class="col-12">
                <q-input
                  outlined
                  dense
                  type="textarea"
                  v-model="csvModel"
                  label="CSV"
                  :placeholder="`Från1, Till1, Spårantal\nFrån2, Till2, Spårantal`"
                />
              </div>
            </div>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn
              flat
              label="Avbryt"
              v-close-popup
              :disable="loadingCSVImport"
            />
            <q-btn
              flat
              label="Bekräfta"
              color="primary"
              @click="onCSVImport"
              :loading="loadingCSVImport"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </template>
    <template v-slot:form="{ formMode }">
      <div class="row q-pa-md q-col-gutter-md" style="width: 450px">
        <div class="col-4">
          <q-input
            outlined
            autocomplete="off"
            dense
            v-model="model.from"
            label="Från"
            :disable="formMode !== 'create'"
            :rules="[requiredRule]"
          />
        </div>
        <div class="col-4">
          <q-input
            outlined
            autocomplete="off"
            dense
            v-model="model.to"
            :disable="formMode !== 'create'"
            label="Till"
            :rules="[requiredRule]"
          />
        </div>
        <div class="col-4">
          <q-input
            outlined
            autocomplete="off"
            dense
            type="number"
            v-model.number="model.track_count"
            label="Spårantal"
            :rules="[requiredNotNegative]"
          />
        </div>
      </div>
    </template>
  </MasterTable>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import { QTable } from 'quasar'
import MasterTable from '@/components/master/MasterTable.vue'
import {
  required as requiredRule,
  minCharactersRule,
  requiredNotNegative,
} from '@/common/formValidationRules'
import { useTrackCount } from '@/composable/useTrackCount'
import { TrackCount } from '@/api/track-count/getTrackCount'

export default defineComponent({
  name: 'TrackCount',

  components: {
    MasterTable,
  },

  setup() {
    const { loading, data, add, remove, update } = useTrackCount(true)

    const model = ref<TrackCount>({
      from: '',
      to: '',
      track_count: 1,
    })

    function resetFormModel() {
      model.value = { from: '', to: '', track_count: 1 }
    }

    const columns: QTable['columns'] = [
      {
        name: 'from',
        label: 'Från',
        align: 'left',
        field: 'from',
        sortable: true,
      },
      {
        name: 'to',
        label: 'Till',
        align: 'left',
        field: 'to',
        sortable: true,
      },
      {
        name: 'track_count',
        label: 'Spårantal',
        align: 'left',
        field: 'track_count',
        sortable: true,
      },
    ]

    const exportData = computed(() => {
      interface ExportRow {
        [key: string]: unknown
      }

      return data.value.map((TrackCount) => {
        return columns.reduce<ExportRow>((acc, column) => {
          const value = TrackCount[column.field as keyof TrackCount]
          acc[column.label] =
            column.format && value ? column.format(value, null) : value
          return acc
        }, {})
      })
    })

    const showCSVModal = ref(false)
    const csvModel = ref('')

    const parseCsv = (csv: string) => {
      return csv
        .trim() // Remove any leading/trailing empty lines
        .split('\n') // Split into individual lines
        .map((line) => {
          const [from, to, trackCount] = line
            .split(',')
            .map((item) => item.trim())

          return {
            from,
            to,
            track_count: parseInt(trackCount, 10),
          }
        })
    }
    const loadingCSVImport = ref(false)
    const onCSVImport = async () => {
      loadingCSVImport.value = true
      const payload = parseCsv(csvModel.value)
      if (payload.length) {
        await add(payload)
      }
      showCSVModal.value = false
      loadingCSVImport.value = false
    }

    watch(showCSVModal, () => {
      csvModel.value = ''
    })

    return {
      minCharactersRule: minCharactersRule(8),
      columns,
      model,
      requiredRule,
      resetFormModel,
      requiredNotNegative,
      data,
      exportData,
      loading,
      add,
      update,
      remove,
      showCSVModal,
      onCSVImport,
      csvModel,
      loadingCSVImport,
    }
  },
})
</script>
